// src/pages/QuizPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../Layout/Layout';
import {
  FiLock,
  FiClock,
  FiAlertCircle,
  FiX,
  FiArrowRight,
  FiAward,
  FiRefreshCw,
  FiShield
} from 'react-icons/fi';
import api from '../utils/apiClient';
import useAntiCheat from '../hooks/useAntiCheat';
import SecurityAlert from '../components/SecurityAlert';

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const QuizHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const QuizTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const QuizDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const QuizMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const QuizInstructions = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  
  h3 {
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.fonts.sizes.md};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    
    svg {
      margin-right: ${({ theme }) => theme.spacing[2]};
    }
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const QuizContent = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const QuizProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProgressText = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  span {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin: 0 ${({ theme }) => theme.spacing[4]};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: ${({ progress }) => `${progress}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  transition: width 0.3s ease;
`;

const QuestionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const QuestionText = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const AnswersList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const AnswerOption = styled.div`
  position: relative;
  background-color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
    if (!showAnswers) {
      return selected ? theme.colors.cardGlow : theme.colors.backgroundLighter;
    } else {
      if (correct) return `${theme.colors.success}20`;
      if (incorrect) return `${theme.colors.error}20`;
      return theme.colors.backgroundLighter;
    }
  }};
  border: 1px solid ${({ theme, selected, correct, incorrect, showAnswers }) => {
    if (!showAnswers) {
      return selected ? theme.colors.primary : theme.colors.divider;
    } else {
      if (correct) return theme.colors.success;
      if (incorrect) return theme.colors.error;
      return theme.colors.divider;
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  cursor: ${({ showAnswers }) => showAnswers ? 'default' : 'pointer'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    ${({ showAnswers, theme }) => !showAnswers && `
      transform: translateY(-2px);
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.cardGlow};
    `}
  }
`;

const AnswerLabel = styled.div`
  display: flex;
  align-items: center;
`;

const OptionLabel = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
    if (!showAnswers) {
      return selected ? theme.colors.primary : theme.colors.backgroundLight;
    } else {
      if (correct) return theme.colors.success;
      if (incorrect) return theme.colors.error;
      return theme.colors.backgroundLight;
    }
  }};
  color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
    if (!showAnswers) {
      return selected ? theme.colors.background : theme.colors.textPrimary;
    } else {
      if (correct || incorrect) return theme.colors.background;
      return theme.colors.textPrimary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: ${({ theme }) => theme.spacing[3]};
`;

const AnswerText = styled.div`
  flex: 1;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme, primary }) =>
    primary ? theme.colors.primary : theme.colors.backgroundLighter};
  color: ${({ theme, primary }) =>
    primary ? theme.colors.background : theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ primary }) => primary ? '600' : '400'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background-color: ${({ theme, primary }) =>
    primary ? theme.colors.primaryDark : theme.colors.backgroundLight};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
  }
`;

const ResultsContainer = styled.div`
  text-align: center;
`;

const ResultIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, passed }) =>
    passed ? theme.colors.success : theme.colors.error};
  color: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  
  svg {
    font-size: 36px;
  }
`;

const ResultTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme, passed }) =>
    passed ? theme.colors.success : theme.colors.error};
`;

const ResultText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ScoreCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, passed }) =>
    passed ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  border: 4px solid ${({ theme, passed }) =>
    passed ? theme.colors.success : theme.colors.error};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme, passed }) =>
    passed ? theme.colors.success : theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  
  span {
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-top: ${({ theme }) => theme.spacing[1]};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme, primary }) =>
    primary ? theme.colors.primary : 'transparent'};
  color: ${({ theme, primary }) =>
    primary ? theme.colors.background : theme.colors.primary};
  border: ${({ theme, primary }) =>
    primary ? 'none' : `1px solid ${theme.colors.primary}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${({ theme, primary }) =>
    primary ? theme.colors.primaryDark : `${theme.colors.primary}10`};
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

// Styled component for the anti-cheat badge with a cybersecurity theme
const SecureEnvironmentBadge = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme, active }) =>
    active ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  color: ${({ theme, active }) =>
    active ? theme.colors.success : theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Courier New', monospace;
  border: 1px solid ${({ theme, active }) =>
    active ? `${theme.colors.success}40` : `${theme.colors.error}40`};
  box-shadow: 0 0 10px ${({ theme, active }) =>
    active ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const QuizPage = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const autoSubmitTriggered = useRef(false); // Prevent multiple auto-submissions

  const [quizData, setQuizData] = useState(null);
  const [randomizedQuestions, setRandomizedQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [optionMappings, setOptionMappings] = useState({});
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [securityAlert, setSecurityAlert] = useState({
    show: false,
    severity: 'warning',
    title: '',
    message: '',
    code: ''
  });
  const [secureEnvironment, setSecureEnvironment] = useState(true);

  // Handle the auto-submission when max violations are reached
  const handleMaxViolations = () => {
    console.log('handleMaxViolations called - quizCompleted:', quizCompleted, 'autoSubmitTriggered:', autoSubmitTriggered.current);
    
    if (!quizCompleted && !autoSubmitTriggered.current) {
      autoSubmitTriggered.current = true;
      console.log('Auto-submit triggered due to max violations - setting 3 second timer');
      
      setSecurityAlert({
        show: true,
        severity: 'critical',
        title: 'SECURITY PROTOCOL BREACH',
        message: 'Multiple security violations detected. Assessment session terminated. Your answers have been collected and the incident has been logged.',
        code: 'FORCED_TERMINATION_PROTOCOL_ENGAGED'
      });

      setTimeout(() => {
        console.log('setTimeout executed - calling submitQuiz now');
        submitQuiz();
      }, 3000);
    } else {
      console.log('Auto-submit blocked - conditions not met (quizCompleted or already triggered)');
    }
  };

  // Handle violations
  const handleViolation = (type, details) => {
    setSecureEnvironment(false);

    // Show the security alert
    setSecurityAlert({
      show: true,
      severity: type === 'tab_switch' ? 'critical' : 'warning',
      title: type === 'tab_switch' ? 'PERIMETER BREACH' : 'DATA EXFILTRATION ATTEMPT',
      message: details.message,
      code: details.code
    });

    // Reset secure environment status after alert is closed
    setTimeout(() => {
      setSecureEnvironment(true);
    }, 5000);
  };

  // Use the anti-cheat hook
  const { violations, resetViolations } = useAntiCheat({
    maxViolations: 3,  // Configure to submit after 3 violations
    onMaxViolationsReached: quizStarted && !quizCompleted ? handleMaxViolations : () => { },
    onViolation: quizStarted && !quizCompleted ? handleViolation : () => { },
    detectRightClick: quizStarted && !quizCompleted,
    detectCopy: quizStarted && !quizCompleted,
    detectPaste: quizStarted && !quizCompleted,
    detectPrint: quizStarted && !quizCompleted,
    detectDevTools: quizStarted && !quizCompleted,
    detectTabSwitch: quizStarted && !quizCompleted,
    detectScreenCapture: quizStarted && !quizCompleted
  });

  // Keep a ref of violations to ensure we always submit the latest count
  const violationsRef = useRef(0);
  useEffect(() => {
    violationsRef.current = violations;
  }, [violations]);

  const fetchQuizData = async () => {
    setLoading(true);
    // Reset auto-submit flag when fetching new quiz data
    autoSubmitTriggered.current = false;
    
    try {
      // Call the API to get the quiz data
      const response = await api.quizzes.getModuleQuiz(moduleId);
      setQuizData(response);

      // Randomize the options for each question
      randomizeOptions(response.questions);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching quiz data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizData();
  }, [moduleId]);

  // Clear security alerts when quiz completes
  useEffect(() => {
    if (quizCompleted) {
      setSecurityAlert({
        show: false,
        severity: 'warning',
        title: '',
        message: '',
        code: ''
      });
    }
  }, [quizCompleted]);

  // Function to randomize option order for each question
  const randomizeOptions = (questions) => {
    const randomized = [];
    const mappings = {};

    questions.forEach(question => {
      // Create array of options with their original keys
      const options = [
        { key: 'a', value: question.option_a },
        { key: 'b', value: question.option_b },
        { key: 'c', value: question.option_c },
        { key: 'd', value: question.option_d }
      ];

      // Shuffle options
      const shuffledOptions = [...options].sort(() => Math.random() - 0.5);

      // Create new question object with shuffled options
      const randomizedQuestion = {
        ...question,
        option_a: shuffledOptions[0].value,
        option_b: shuffledOptions[1].value,
        option_c: shuffledOptions[2].value,
        option_d: shuffledOptions[3].value
      };

      // Store mapping from displayed option to original option
      mappings[question.id] = {
        a: shuffledOptions[0].key,
        b: shuffledOptions[1].key,
        c: shuffledOptions[2].key,
        d: shuffledOptions[3].key
      };

      randomized.push(randomizedQuestion);
    });

    setRandomizedQuestions(randomized);
    setOptionMappings(mappings);
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
    autoSubmitTriggered.current = false; // Reset auto-submit flag
    // Reset violations counter when starting a quiz
    resetViolations();
  };

  const handleSelectAnswer = (questionId, displayedOption) => {
    // Map the displayed option (a, b, c, d) to the original option using our mapping
    const originalOption = optionMappings[questionId][displayedOption];

    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: originalOption
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestion < randomizedQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Last question - submit the quiz
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    console.log('submitQuiz called - submitting:', submitting, 'quizCompleted:', quizCompleted);
    
    if (submitting) {
      console.log('Submit blocked - already submitting');
      return;
    }

    console.log('Proceeding with quiz submission...');
    setSubmitting(true);

    try {
      // Use ref to get the most current violation count
      const currentViolations = violationsRef.current;
      const answerCount = Object.keys(selectedAnswers).length;
      console.log('Submitting quiz with violations:', currentViolations, 'answers count:', answerCount);

      // If no answers (auto-submit due to violations), create failed result
      if (answerCount === 0) {
        console.log('No answers provided - creating failed result due to security violations');
        const totalQuestions = randomizedQuestions.length || 0;
        const results = {
          passed: false,
          score: 0,
          total_questions: totalQuestions,
          correct_answers: 0,
          incorrect_answers: 0,
          unanswered: totalQuestions,
          violations_count: currentViolations,
          message: 'Quiz terminated due to security violations. No answers were submitted.'
        };
        setQuizResults(results);
        setQuizCompleted(true);
        setSubmitting(false);
        return;
      }

      // Submit answers to the API and get results
      const results = await api.quizzes.submitQuiz(moduleId, selectedAnswers, currentViolations);
      console.log('Quiz submitted successfully, results received');

      // Set the quiz results from the API response
      setQuizResults(results);
      setQuizCompleted(true);
    } catch (error) {
      console.error("Error submitting quiz:", error);
      // Handle submission error
      setSecurityAlert({
        show: true,
        severity: 'warning',
        title: 'SUBMISSION ERROR',
        message: 'There was an error submitting your quiz. Please try again.',
        code: 'QUIZ_SUBMISSION_FAILED'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetakeQuiz = async () => {
    try {
      // Reset state
      setSelectedAnswers({});
      setCurrentQuestion(0);
      setQuizCompleted(false);
      setQuizResults(null);
      autoSubmitTriggered.current = false;
      resetViolations(); // Reset violations for retake

      // Fetch new quiz questions
      await fetchQuizData();

      // Restart the quiz
      setQuizStarted(true);
    } catch (error) {
      console.error("Error preparing quiz retake:", error);
      const errorData = error?.response?.data;

      setSecurityAlert({
        show: true,
        severity: 'error',
        title: 'RETAKE ERROR',
        message: errorData?.detail || errorData?.message || 'There was an error preparing your quiz retake. Please try again later.',
        code: 'QUIZ_RETAKE_FAILED'
      });
    }
  };

  const handleContinue = () => {
    // Navigate to the next content or back to module page
    navigate(`/learning/${courseId}/`);
  };

  if (loading) {
    return (
      <Layout>
        <div>Loading quiz...</div>
      </Layout>
    );
  }

  if (!quizData) {
    return (
      <Layout>
        <div>Failed to load quiz data.</div>
      </Layout>
    );
  }

  const { quiz } = quizData;
  const questions = randomizedQuestions;
  const currentQ = questions[currentQuestion];
  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <Layout>
      <QuizContainer>
        {!quizStarted ? (
          <QuizHeader>
            <QuizTitle>{quiz.title}</QuizTitle>
            <QuizDescription>{quiz.description}</QuizDescription>

            <QuizMeta>
              <MetaItem>
                <FiClock />
                Estimated Time: {quiz.estimated_time} minutes
              </MetaItem>
              <MetaItem>
                {questions.length} Questions
              </MetaItem>
              <MetaItem>
                Passing Score: {quiz.passing_score}%
              </MetaItem>
            </QuizMeta>

            <QuizInstructions>
              <h3>
                <FiAlertCircle />
                Instructions
              </h3>
              <p>Select the best answer for each question.</p>
              <p>You need to score at least {quiz.passing_score}% to pass this quiz.</p>
              <p>You can take this quiz multiple times if needed.</p>
              <p><strong>SECURE ENVIRONMENT NOTICE:</strong> This assessment is protected by an anti-tampering system. Leaving the assessment window or attempting to copy/paste content will trigger security protocols and may result in automatic submission.</p>
            </QuizInstructions>

            <StartButton onClick={handleStartQuiz}>
              Start Quiz
            </StartButton>
          </QuizHeader>
        ) : !quizCompleted ? (
          <QuizContent>
            {/* Security badge */}
              <SecureEnvironmentBadge active={secureEnvironment}>
              {secureEnvironment ? <FiLock /> : <FiShield />}
              {secureEnvironment
                ? "SECURE ENVIRONMENT"
                : "SECURITY ALERT"}
              </SecureEnvironmentBadge>

            {/* Security Alert Modal */}
            <SecurityAlert
              isOpen={securityAlert.show}
              onClose={() => setSecurityAlert({ ...securityAlert, show: false })}
              severity={securityAlert.severity}
              title={securityAlert.title}
              message={securityAlert.message}
              code={securityAlert.code}
              buttonText="ACKNOWLEDGE"
            />

            <QuizProgress>
              <ProgressText>
                Question <span>{currentQuestion + 1}</span> of {questions.length}
              </ProgressText>
              <ProgressBar>
                <ProgressFill progress={progressPercentage} />
              </ProgressBar>
            </QuizProgress>

            <QuestionContainer>
              <QuestionText>{currentQ.question}</QuestionText>

              <AnswersList>
                <AnswerOption
                  selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].a}
                  onClick={() => handleSelectAnswer(currentQ.id, 'a')}
                >
                  <AnswerLabel>
                    <OptionLabel selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].a}>A</OptionLabel>
                    <AnswerText>{currentQ.option_a}</AnswerText>
                  </AnswerLabel>
                </AnswerOption>

                <AnswerOption
                  selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].b}
                  onClick={() => handleSelectAnswer(currentQ.id, 'b')}
                >
                  <AnswerLabel>
                    <OptionLabel selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].b}>B</OptionLabel>
                    <AnswerText>{currentQ.option_b}</AnswerText>
                  </AnswerLabel>
                </AnswerOption>

                <AnswerOption
                  selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].c}
                  onClick={() => handleSelectAnswer(currentQ.id, 'c')}
                >
                  <AnswerLabel>
                    <OptionLabel selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].c}>C</OptionLabel>
                    <AnswerText>{currentQ.option_c}</AnswerText>
                  </AnswerLabel>
                </AnswerOption>

                <AnswerOption
                  selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].d}
                  onClick={() => handleSelectAnswer(currentQ.id, 'd')}
                >
                  <AnswerLabel>
                    <OptionLabel selected={selectedAnswers[currentQ.id] === optionMappings[currentQ.id].d}>D</OptionLabel>
                    <AnswerText>{currentQ.option_d}</AnswerText>
                  </AnswerLabel>
                </AnswerOption>
              </AnswersList>
            </QuestionContainer>

            <NavigationButtons>
              <div></div> {/* Spacer for alignment */}
              <NavButton
                onClick={handleNextQuestion}
                disabled={!selectedAnswers[currentQ.id] || submitting}
                primary
              >
                {currentQuestion < questions.length - 1 ? 'Next' : submitting ? 'Submitting...' : 'Submit Quiz'}
                {!submitting && <FiArrowRight />}
              </NavButton>
            </NavigationButtons>
          </QuizContent>
        ) : (
          <QuizContent>
            <ResultsContainer>
              <ResultIcon passed={quizResults.passed}>
                {quizResults.passed ? <FiAward /> : <FiX />}
              </ResultIcon>

              <ResultTitle passed={quizResults.passed}>
                {quizResults.passed ? 'Quiz Passed!' : 'Quiz Failed'}
              </ResultTitle>

              <ResultText>
                {quizResults.passed
                  ? `Congratulations! You have successfully completed this quiz with a score of ${quizResults.score}%.`
                  : `You did not meet the minimum passing score. Please review the material and try again.`}
                {violationsRef.current > 0 && ` The security system detected ${violationsRef.current} security ${violationsRef.current === 1 ? 'violation' : 'violations'} during your assessment session.`}
                {quizResults.module_completed && ' You have completed this module!'}
              </ResultText>

              <ScoreContainer>
                <ScoreCircle passed={quizResults.passed}>
                  {quizResults.score}%
                  <span>Your Score</span>
                </ScoreCircle>
              </ScoreContainer>

              <ActionButtons>
                <ActionButton onClick={handleRetakeQuiz}>
                  <FiRefreshCw />
                  Retake Quiz
                </ActionButton>

                {quizResults.passed && (
                  <ActionButton onClick={handleContinue} primary>
                    <FiArrowRight />
                    Continue
                  </ActionButton>
                )}
              </ActionButtons>
            </ResultsContainer>
          </QuizContent>
        )}
      </QuizContainer>
    </Layout>
  );
};

export default QuizPage;